import { ScrollView, View } from 'react-native';
import React, { useCallback, useEffect, useMemo } from 'react';
import Installment from './components/Installment';
import CheckoutLayout from '../CheckoutLayout';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCheckout, useSalesFunnel } from '~/stores';
import { Text } from '~/components';
import useAppNavigation from '~/hooks/useAppNavigation';
import { useRoute, type RouteProp } from '@react-navigation/native';
import type { RootStackParamList } from '~/navigation/types';
import { CheckoutStepNames } from '~/utils/salesFunnel';

// used for dev
const MOCK_INSTALLMENTS = [
  {
    id: 1,
    description: 'PARCELA MENSAL',
    price: 29.9,
  },
];

const schema = yup
  .object()
  .shape({
    installment: yup.object().required(),
  })
  .required();

interface InstallmentsStep {
  installment: Checkout.Installment;
}

const Installments = () => {
  const { push } = useAppNavigation();
  const { setInstallment, setPage, form } = useCheckout();
  const { newStepLog } = useSalesFunnel();

  const { params } =
    useRoute<RouteProp<RootStackParamList, 'MarisaServicos_Checkout'>>();

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<InstallmentsStep>({
    resolver: yupResolver(schema),
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const onSubmit = useCallback(
    ({ installment }: InstallmentsStep) => {
      setInstallment(installment);

      const nextStep = (params.step || 0) + 1;
      setPage(nextStep);
      push('MarisaServicos_Checkout', { step: nextStep });
    },
    [params.step, push, setInstallment, setPage]
  );

  const installments = useMemo(
    () =>
      form?.paymentMethod?.installments &&
      form?.paymentMethod?.installments.length > 0
        ? form?.paymentMethod?.installments
        : MOCK_INSTALLMENTS,
    [form?.paymentMethod?.installments]
  );

  useEffect(() => {
    newStepLog(CheckoutStepNames.INSTALLMENTS);
  }, [newStepLog]);

  return (
    <CheckoutLayout isValid={isValid} onSubmit={handleSubmit(onSubmit)}>
      <Text className="text-xl text-gray-600 mb-9">
        Como você prefere pagar?
      </Text>

      {form?.paymentMethod?.method === 'bank-slip' && (
        <View className="py-2 px-4 rounded bg-gray-100 mb-4">
          <Text className="text-xs text-gray-400">
            Seu contrato será válido por 12 meses.
          </Text>
        </View>
      )}

      <Controller
        control={control}
        name="installment"
        render={({ field: { value, onChange } }) => (
          <ScrollView showsVerticalScrollIndicator={false} className="h-[50px]">
            {installments.map((installment, index) => (
              <Installment
                {...installment}
                isFirst={index === 0}
                value={value}
                key={String(installment.id)}
                onPress={() => onChange(installment)}
              />
            ))}
          </ScrollView>
        )}
      />
    </CheckoutLayout>
  );
};

export default Installments;
